import { IState } from "..";
import { createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import { groupBy } from "lodash";
import { AppThunk } from "../../app/store";
import PickupService, { REQUEST_ID } from "../../app/data/pickup/pickupService";
import { PickupStatus } from "../../app/data/common/route";
import {
  PickupAssignRequestModel,
  PickupsGroupModel,
  PickupsLogFilterModel,
  PickupsResponseContentModel,
  PickupUpdateResponseModel
} from "../../app/data/pickup/models";
import { initialPickupAssignmentState } from "./PickupAssignmentState";
import { convertToSubPickup, getPickupById, isGroup, isSubPickup } from "../../services/pickups";

const pickupService = PickupService.getInstance();

const createPickupGroupItem = (item: PickupsResponseContentModel) => {
  return {
    id: item.pickup.groupId,
    shipper: {
      address: item.shipper.address,
    },
    pickup: {
      date: item.pickup.date,
      driverId: item.pickup.driverId,
    }
  }
}

const groupById = (data?: PickupsResponseContentModel[]) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const groupedItems = groupBy(
    data.filter(item => item?.pickup?.groupId),
    item => item.pickup.groupId
  );

  const usedGroups = new Set();

  const groupedData = data.reduce((result: any[], item: any) => {
    if (!item?.pickup) return result;

    if (!item.pickup.groupId || !groupedItems[item.pickup.groupId] || groupedItems[item.pickup.groupId].length <= 1) {
      return [
        ...result,
        { ...item, pickup: { ...item.pickup, single: true } }
      ];
    }

    if (usedGroups.has(item.pickup.groupId)) return result;

    usedGroups.add(item.pickup.groupId);

    return [
      ...result,
      {
        ...createPickupGroupItem(item),
        pickups: groupedItems[item.pickup.groupId].map(convertToSubPickup)
      }
    ];
  }, []);

  return groupedData;
};

const updatePickupFields = (pickup: PickupsResponseContentModel, newPickup: PickupUpdateResponseModel, username: string): PickupsResponseContentModel => (
  {
    ...pickup,
    status: newPickup.pickupStatus,
    pickup: {
      ...pickup.pickup,
      driverId: newPickup.driverId,
      driverName: newPickup.driverName,
      groupId: newPickup.groupId,
      pickupStatus: newPickup.pickupStatus,
    },
    pickupFullData: {
      ...pickup.pickupFullData,
      pickupRequest: {
        ...pickup.pickupFullData.pickupRequest,
        pickupStatus: newPickup.pickupStatus,
        driverId: newPickup.driverId,
        manifestNumber: newPickup.manifestNumber,
        dateUpdated: moment().format("MM/DD/YYYY hh:mm:ss"),
        userUpdated: username,
      },
    }
  }
);

export const pickupAssignmentSlice = createSlice({
  name: "pickupAssignment",
  initialState: initialPickupAssignmentState,
  reducers: {
    resetPickupAssignmentState: (state) => initialPickupAssignmentState,
    requestStarted: (state, { payload }) => {
      if (payload === "GET_PICKUPS_PORTION" || payload === "GET_DRIVERS_PORTION" || payload === "GET_PICKUPS_LOGS_PORTION") {
        state.fetchPortionStarted = true;
      } else {
        state.requestStarted = true;
      }
      state.requestSucceed = false;
      state.requestFailed = false;
      state.requestCreator = payload;
      state.requestError = "";
    },
    requestSucceed: (state) => {
      state.requestStarted = false;
      state.fetchPortionStarted = false;
      state.requestSucceed = true;
      state.requestFailed = false;
    },
    requestFailed: (state, { payload }) => {
      state.requestStarted = false;
      state.fetchPortionStarted = false;
      state.requestSucceed = false;
      state.requestFailed = true;
      state.requestError = payload;
    },
    setRequestResponseCode: (state, { payload }) => {
      state.requestResponseCode = payload;
    },
    resetPickups: (state) => {
      state.pickups = [];
      state.pickupsRequest = null;
      state.pickupsFetchedAll = false;
    },
    setPickups: (state, { payload }) => {
      state.pickups = groupById(payload.content);
      state.pickupsRequest = payload.scroll;
      state.pickupsFetchedAll = payload.scroll?.idsMap?.hasNext !== "true";
    },
    addPickups: (state, { payload }) => {
      state.pickups = [...state.pickups, ...groupById(payload.content)];
      state.pickupsRequest = payload.scroll;
      state.pickupsFetchedAll = payload.scroll?.idsMap?.hasNext !== "true";
    },
    updatePickup: (state, { payload }) => {
      // update locally to show changes immediately
      const pickup = getPickupById(state.pickups, payload.pickupId);

      if (!isSubPickup(pickup)) {
        let index = state.pickups.findIndex(obj => !isGroup(obj) && obj.pickup.pickupId === payload.pickupId);
        const groupId = payload.groupId || pickup.pickup.groupId;

        state.pickups[index] = updatePickupFields(pickup, {
          ...payload,
          groupId,
          pickupStatus: payload.status,
        }, payload.username);
      } else {
        const groupIndex = state.pickups.findIndex(obj => isGroup(obj) && obj.id === pickup.pickup.groupId);
        const pickups = [...(state.pickups[groupIndex] as PickupsGroupModel).pickups];
        const index = pickups.findIndex(obj => obj.pickup.pickupId === payload.pickupId);
        const groupId = payload.groupId || pickups[index].pickup.groupId;

        pickups[index] = updatePickupFields(
          pickup,
          {
            ...payload,
            groupId,
            pickupStatus: payload.status,
          },
          payload.username,
        );

        state.pickups.splice(groupIndex, 1, ...groupById(pickups));
        state.createdGroupId = "";
      }
    },
    updatePickupsGroups: (state, { payload }) => {
      const index = payload.groupId
        ? state.pickups.findIndex(obj => isGroup(obj) && obj.id === payload.groupId)
        : state.pickups.findIndex(obj => !isGroup(obj) && obj.pickup.pickupId === payload.data[0].id);

      const pickups: PickupsResponseContentModel[] = payload.groupId
        ? (state.pickups[index] as PickupsGroupModel).pickups
        : [state.pickups[index]];

      const updatedPickups = pickups.map((pickup) => {
        const newPickup = payload.data.find((item: PickupUpdateResponseModel) => item.id === pickup.pickup.pickupId);
        return newPickup ? updatePickupFields(pickup, newPickup, payload.username) : pickup;
      });

      const newGroupId = payload.data.find((item: PickupUpdateResponseModel) => item.groupId !== payload.groupId).groupId || "";
      const newGroupIndex = state.pickups.findIndex(obj => (isGroup(obj) && obj.id === newGroupId) || (!isGroup(obj) && obj.pickup.groupId === newGroupId));

      if (newGroupIndex < 0 || newGroupIndex === index) {
        // single pickup or the whole group was changed and keeps its position in table
        state.pickups.splice(index, 1, ...groupById(updatedPickups));
      } else {
        // single pickup or part of the group was changed and should be attached to existing group

        // remove changed pickups from their group or remove outdated single pickup from its position
        state.pickups.splice(index, 1, ...groupById(updatedPickups.filter(pickup => pickup.pickup.groupId === payload.groupId)));

        // index can change after removing items
        const updateIndex = state.pickups.findIndex(obj => (isGroup(obj) && obj.id === newGroupId) || (!isGroup(obj) && obj.pickup.groupId === newGroupId));

        if (isGroup(state.pickups[updateIndex])) {
          // add changed pickups into existing group
          (state.pickups[updateIndex] as PickupsGroupModel).pickups = [
            ...(state.pickups[updateIndex] as PickupsGroupModel).pickups,
            ...updatedPickups.filter((pickup) => pickup.pickup.groupId === newGroupId).map(convertToSubPickup),
          ]
        } else {
          // combine changed pickups with existing single pickup into a new group
          state.pickups.splice(updateIndex, 1, ...groupById([
            state.pickups[updateIndex],
            ...updatedPickups.filter(pickup => pickup.pickup.groupId === newGroupId),
          ]));
        }
      }

      state.createdGroupId = newGroupId;
    },
    setDrivers: (state, { payload }) => {
      if (!payload) return;
      state.drivers = payload;
    },
    setDriver: (state, { payload }) => {
      if (!payload) return;
      state.drivers = [{
        ...payload,
        driverId: payload.driverId
      }];
    },
    clearDrivers: (state) => {
      state.drivers = [];
    },
    setPickupsLog: (state, { payload }) => {
      state.pickupsLog = payload;
    },
    addPickupsLog: (state, { payload }) => {
      state.pickupsLog = [...state.pickupsLog, ...payload];
    },
    clearPickupsLog: (state) => {
      state.pickupsLog = [];
    },
    setPickupsLogRequest: (state, { payload }) => {
      state.pickupsLogRequest = {
        page: payload.page,
        last: payload.last
      }
    },
    setLoadedPages: (state, { payload }) => {
      state.loadedPickupAssignmentPages = [payload];
    },
    addLoadedPages: (state, { payload }) => {
      state.loadedPickupAssignmentPages = [...state.loadedPickupAssignmentPages, payload];
    },
    setStartDate: (state, { payload }) => {
      state.startDate = payload;
    },
    setEndDate: (state, { payload }) => {
      state.endDate = payload;
    }
  }
});

export const {
  resetPickupAssignmentState,
  requestStarted,
  requestSucceed,
  requestFailed,
  setRequestResponseCode,
  resetPickups,
  setPickups,
  addPickups,
  updatePickup,
  updatePickupsGroups,
  setDrivers,
  setDriver,
  clearDrivers,
  setPickupsLog,
  addPickupsLog,
  clearPickupsLog,
  setPickupsLogRequest,
  setLoadedPages,
  addLoadedPages,
  setStartDate,
  setEndDate,
} = pickupAssignmentSlice.actions;

export const pickupAssignmentSelector = (state: IState) => {
  return state.pickupAssignment;
};

const fetchPickups = (
  terminal: number | null,
  filter: {
    statuses: PickupStatus[],
    expiredOnly: boolean,
    showFuture: boolean,
    search: string,
    dateRange?: { startDate: string, endDate: string },
  },
  pagination?: { lastIds?: string, pageSize?: number },
  nextPage?: boolean,
): AppThunk => async (dispatch) => {
  dispatch(requestStarted(nextPage ? "GET_PICKUPS_PORTION" : "GET_PICKUPS"));
  const response = await pickupService.getPickups(terminal, filter, pagination);
  // A superseded request was canceled by a newer one (e.g. rapid filter changes).
  // Ignore it so it can't overwrite the newer request's result with a failure/stale state.
  if (response.isCancel?.()) return;
  dispatch(setRequestResponseCode(response.status));
  if (response.ok()) {
    if (response.status !== 204) {
      dispatch(nextPage ? addPickups(response.data) : setPickups(response.data));
    } else {
      dispatch(resetPickups());
    }
    dispatch(requestSucceed());
    dispatch(nextPage ? addLoadedPages(response.data.scroll) : setLoadedPages(response.data.scroll))
  } else {
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
  }
};


export const getPickups = (
  terminal: number | null,
  filter: {
    statuses: PickupStatus[],
    expiredOnly: boolean,
    showFuture: boolean,
    search: string,
    dateRange?: { startDate: string, endDate: string },
  },
  lastIds?: string
): AppThunk => async (dispatch) => {
  dispatch(fetchPickups(terminal, filter, { lastIds }, !!lastIds));
};

export const reloadPickupAssignmentPages = (
  terminal: number | null,
  filter: {
    statuses: PickupStatus[],
    expiredOnly: boolean,
    search: string,
  },
): AppThunk => async (dispatch, getState) => {
  const pages = getState().pickupAssignment.loadedPickupAssignmentPages;
  const pageSize = pages.reduce((result: number, page: { pageSize: number }) => (result + page.pageSize), 0);
  const pagination = { pageSize };
  dispatch(fetchPickups(terminal, filter, pagination, false));
};

export const getDrivers = (
  zip: string,
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("GET_DRIVERS"));
  pickupService.cancelRequest(REQUEST_ID.GET_DRIVER);
  const response = await pickupService.getDrivers(zip);
  if (response.isCancel?.()) return;
  if (response.ok()) {
    dispatch(setDrivers(response.data));
    dispatch(requestSucceed());
  } else {
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
  }
};

export const getDriverById = (
  id: number,
  onSuccess: (name: string) => void,
  onlyCallback?: boolean
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("GET_DRIVER"));
  pickupService.cancelRequest(REQUEST_ID.GET_DRIVERS);
  const response = await pickupService.getDriver(id);

  if (response.isCancel?.()) return;

  if (response.ok()) {
    !onlyCallback && dispatch(setDriver(response.data ? response.data : null));
    dispatch(requestSucceed());
    onSuccess(response.data ? response.data?.driverName : (onlyCallback ? "" : "N/A"));
  } else {
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
  }
};

export const resetDrivers = (): AppThunk => async (dispatch) => {
  pickupService.cancelRequest(REQUEST_ID.GET_DRIVER);
  pickupService.cancelRequest(REQUEST_ID.GET_DRIVERS);
  dispatch(requestSucceed());
  dispatch(clearDrivers());
}

export const changePickups = (
  request: PickupAssignRequestModel[],
  username: string,
  groupId: string,
  onSuccess: () => void,
  onFail: (error: string) => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("CHANGE_PICKUP_ASSIGNMENT"));
  const response = await pickupService.changePickups(request);
  if (response.ok()) {
    dispatch(requestSucceed());
    dispatch(updatePickupsGroups({ data: response.data, groupId, username }));
    onSuccess();
  } else {
    onFail(response.getError ? `${response.getError()}` : "Error");
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
  }
};

export const getPickupsLog = (
  filter: PickupsLogFilterModel,
  page?: number
): AppThunk => async (dispatch) => {
  dispatch(requestStarted(page ? "GET_PICKUPS_LOGS_PORTION" : "GET_PICKUPS_LOG"));
  const response = await pickupService.getPickupsLog(filter, page && page);
  if (response.isCancel?.()) return;
  if (response.ok()) {
    dispatch(page ? addPickupsLog(response.data?.content) : setPickupsLog(response.data?.content));
    dispatch(setPickupsLogRequest({
      page: response.data?.pageable.pageNumber,
      last: response.data?.last
    }));
    dispatch(requestSucceed());
  } else {
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
  }
};

const pickupAssignmentReducer = pickupAssignmentSlice.reducer;
export default pickupAssignmentReducer;
